import * as React from 'react';
import classnames from 'classnames';

import PaginationInput from './input/PaginationInput';

import { navigate } from 'libs/router';

import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';

import type { LocationWindow, PaginationLinks, PaginationMeta } from 'types';

import './pagination.scss';

import type { SyntheticEvent } from 'react';

type LinkProps = {
  link?: string,
  'data-page': number,
  onClick?: (event: SyntheticEvent<HTMLAnchorElement>) => void
};

type PaginationPropsWithFallbacks = {
  currentPage: number,
  itemsCurrentPage: number,
  itemsPerPage: number,
  itemsTotal: number,
  pagesTotal: number,
  hasPreviousPage: boolean,
  hasNextPage: boolean
};

type Props = {
  meta?: PaginationMeta,
  links?: PaginationLinks,
  location: LocationWindow,
  className: string,
  scrollToTopOnPageChange: boolean,
  onClick?: (pageNumber: number) => void
};

function getPaginationPropsWillFallbacks(props: Props): PaginationPropsWithFallbacks {
  const meta = props.meta || {};
  const links = props.links || {};

  return {
    currentPage: meta.currentPage || 0,
    itemsCurrentPage: meta.itemsCurrentPage || 0,
    itemsPerPage: meta.itemsPerPage || 0,
    itemsTotal: Number(meta.itemsTotal || 0),
    pagesTotal: meta.pagesTotal || 0,
    hasPreviousPage: Boolean(links.prev),
    hasNextPage: Boolean(links.next)
  };
}

class Pagination extends React.PureComponent<Props> {
  static defaultProps = {
    className: '',
    scrollToTopOnPageChange: true
  };

  getLinkProps(pageNumber: number, isDisabled: boolean = false): LinkProps {
    return {
      'onClick': (isDisabled) ? undefined : this.handleClick,
      'data-page': pageNumber
    };
  }

  handleClick = (event: SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { onClick, scrollToTopOnPageChange, location } = this.props;
    const target = event.currentTarget;
    const pageNumber = Number(target.dataset.page);

    if (scrollToTopOnPageChange) {
      window.scrollTo(0, 0);
    }

    if (onClick) {
      onClick(pageNumber);
    } else {
      navigate.to(getLocationUrl(getLocationWithExtendedQuery(location, {
        page: pageNumber
      })));
    }
  };

  renderStepPage(propsWillFallbacks: PaginationPropsWithFallbacks, key: string): React.ReactNode {
    const { currentPage, hasPreviousPage, hasNextPage } = propsWillFallbacks;

    const prevClassNames = classnames(
      'pagination__step',
      'pagination__step--prev', {
        'pagination__step--disabled': !hasPreviousPage
      }
    );

    const nextClassNames = classnames(
      'pagination__step',
      'pagination__step--next', {
        'pagination__step--disabled': !hasNextPage
      }
    );

    return (
      <div
        key={`pagination-item-${key}-${currentPage}`}
        className="pagination__item"
      >
        {
          key === 'prev' &&
            <a
              className={prevClassNames}
              {...this.getLinkProps(currentPage - 1, !hasPreviousPage)}
            >
              &lsaquo;
            </a>
        }
        {
          key === 'next' &&
            <a
              className={nextClassNames}
              {...this.getLinkProps(currentPage + 1, !hasNextPage)}
            >
              &rsaquo;
            </a>
        }
      </div>
    );
  }

  renderPage(
    propsWillFallbacks: PaginationPropsWithFallbacks,
    key: string,
    pageNumber: number
  ): React.ReactNode {
    const { currentPage } = propsWillFallbacks;
    const isCurrentPage = pageNumber === currentPage;

    return (
      <div
        key={`pagination-item-${key}`}
        className="pagination__item"
      >
        {
          isCurrentPage ?
            <span className="pagination__page pagination__page--active">
              {pageNumber}
            </span> :
            <a
              className="pagination__page pagination__page--inactive"
              {...this.getLinkProps(pageNumber)}
            >
              {pageNumber}
            </a>
        }
      </div>
    );
  }

  renderPages(propsWillFallbacks: PaginationPropsWithFallbacks): React.ReactNode | undefined | null {
    const { currentPage, pagesTotal } = propsWillFallbacks;
    const pagesGroupSize = 2;

    const pages: Array<React.ReactNode> = [];

    let min;
    let max;

    if (currentPage <= pagesGroupSize) {
      min = 1;
      max = Math.min(5, pagesTotal);
    } else if (currentPage > pagesTotal - pagesGroupSize) {
      min = Math.max(1, pagesTotal - 5);
      max = pagesTotal;
    } else {
      min = Math.max(1, currentPage - pagesGroupSize);
      max = Math.min(pagesTotal, currentPage + pagesGroupSize);
    }

    for (let i = min; i <= max; i++) {
      pages.push(this.renderPage(propsWillFallbacks, `page-${i}`, i));
    }

    return pages;
  }

  render(): React.ReactNode {
    const propsWillFallbacks = getPaginationPropsWillFallbacks(this.props);
    const { className, onClick } = this.props;
    const { pagesTotal, currentPage, itemsCurrentPage } = propsWillFallbacks;

    if (currentPage === 0 || pagesTotal <= 1 || itemsCurrentPage === 0) {
      return null;
    }

    const paginationClasses = classnames({
      pagination: true,
      [className]: className
    });

    return (
      <div className={paginationClasses}>
        <div className="pagination__nav">
          {this.renderStepPage(propsWillFallbacks, 'prev')}
          <div className="pagination__inner">
            {this.renderPages(propsWillFallbacks)}
          </div>
          {this.renderStepPage(propsWillFallbacks, 'next')}
        </div>
        <PaginationInput
          pagesTotal={propsWillFallbacks.pagesTotal}
          onUpdate={onClick}
        />
      </div>
    );
  }
}

export default Pagination;
